/**
 * PendingApprovalPage — Tres estados:
 *  • "pending"  → Esperando aprobación del director
 *  • "rejected" → Rechazado; puede reintentar
 *  • null/approved → Redirige al dashboard
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  RefreshCw,
  LogOut,
  Loader2,
  XCircle,
  ArrowRight,
  CheckCircle2,
  User,
  Phone,
  Calendar,
  MapPin,
  UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui";
import { useAuthStore } from "@/stores/authStore";
import { joinRequestService } from "@/lib/services/joinRequests";
import type { JoinRequest } from "@/lib/services/joinRequests";
import { formatDate } from "@/lib/utils";
import { cancelRequest } from "@/lib/services/joinRequests";

export default function PendingApprovalPage() {
  const navigate = useNavigate();
  const { user, profile, signOut } = useAuthStore();
  const [request, setRequest] = useState<JoinRequest | null | undefined>(
    undefined,
  ); // undefined = loading
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [confirmingWithdraw, setConfirmingWithdraw] = useState(false);
  const withdrawingRef = useRef(false);

  // Observa la solicitud más reciente (pending o rejected)
  useEffect(() => {
    const currentUserId = profile?.id || user?.uid;
    if (!currentUserId) return;
    const unsub = joinRequestService.watchUserLatestRequest(
      currentUserId,
      (req) => {
        if (!req) {
          // Sin solicitud activa → fue aprobado (Member record creado por director)
          // Pero si estamos en proceso de retiro, no redirigir a dashboard
          if (withdrawingRef.current) return;
          // Dejamos que authStore re-evalue; hacemos reload completo
          window.location.href = "/dashboard";
          return;
        }
        setRequest(req);
      },
      (err) => console.error("watchUserLatestRequest:", err),
    );
    return () => unsub();
  }, [profile?.id, user?.uid]);

  const handleRetry = async () => {
    if (!request?.id) return;
    setRetrying(true);
    setRetryError(null);
    withdrawingRef.current = true;
    try {
      await cancelRequest(request.id);
      navigate("/setup");
    } catch (err) {
      console.error("cancelRequest failed:", err);
      withdrawingRef.current = false;
      setRetryError("No se pudo cancelar la solicitud. Intenta de nuevo.");
      setRetrying(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login");
  };

  // Re-evalúa el listener sin recargar la página (el listener ya está activo)
  const handleCheckVerification = () => {
    // El watchUserLatestRequest ya actualiza el estado en tiempo real.
    // Forzar re-evaluación del perfil auth por si el token está desactualizado.
    if (profile?.id) {
      setRequest(undefined); // muestra spinner brevemente
      setTimeout(() => {
        // Si el listener no dispara de nuevo en 2 s, probablemente ya está aprobado
        window.location.reload();
      }, 2000);
    }
  };

  // ── Loading ──────────────────────────────────────────────
  if (request === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-400 animate-spin" />
      </div>
    );
  }

  // ── Rechazado ────────────────────────────────────────────
  if (request?.status === "rejected") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-red-100 flex items-center justify-center">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Solicitud no aprobada
          </h2>

          <p className="text-gray-500 mb-2">
            El director de{" "}
            <span className="font-semibold text-gray-700">
              {request.clubName}
            </span>{" "}
            no aprobó tu solicitud de ingreso.
          </p>

          {request.rejectionReason && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-left text-red-700">
              <p className="font-medium mb-1">Motivo:</p>
              <p>{request.rejectionReason}</p>
            </div>
          )}

          {!request.rejectionReason && (
            <p className="text-sm text-gray-400 mb-6">
              No se indicó un motivo. Puedes contactar al director de tu club
              para más información.
            </p>
          )}

          <div className="space-y-3">
            <Button
              onClick={handleRetry}
              className="w-full"
              disabled={retrying}
            >
              {retrying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4 mr-2" />
              )}
              Intentar con otro club
            </Button>
            {retryError && (
              <p className="text-sm text-red-600 text-center">{retryError}</p>
            )}

            <button
              onClick={handleSignOut}
              className="flex items-center justify-center gap-2 w-full text-sm text-gray-400 hover:text-gray-600 py-2"
            >
              <LogOut className="w-4 h-4" />
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Pendiente ────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-primary-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-4">
        {/* Card principal */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-amber-100 flex items-center justify-center">
            <Clock className="w-10 h-10 text-amber-500" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Solicitud enviada
          </h2>
          <p className="text-gray-500 mb-1">
            {request?.requestedRole === "section_director"
              ? "Esperando aprobación del director general o el administrador de la asociación de"
              : "Esperando aprobación de un director del club o el administrador de la asociación de"}
          </p>
          <p className="text-lg font-semibold text-gray-800 mb-5">
            {request?.clubName || "el club"}
          </p>

          <p className="text-xs text-gray-500 mb-6 bg-blue-50 border border-blue-100 rounded-lg p-3">
            {request?.requestedRole === "section_director" ? (
              <>
                <b>¿Quién puede aprobarme?</b> El director general del club o el
                administrador de la asociación. Nombrar directores de sección es
                una decisión que requiere esa autoridad.
              </>
            ) : (
              <>
                <b>¿Quién puede aprobarme?</b> Cualquier director activo del
                club (general o de sección) o el administrador de la asociación.
                Si nadie responde pronto, coordina con la asociación por
                WhatsApp para que revisen tu solicitud.
              </>
            )}
          </p>

          {/* Timeline de estado */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="flex flex-col items-center gap-1">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
              </div>
              <span className="text-xs text-gray-400">Enviado</span>
            </div>
            <div className="h-px w-8 bg-amber-200" />
            <div className="flex flex-col items-center gap-1">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center animate-pulse">
                <Clock className="w-4 h-4 text-amber-500" />
              </div>
              <span className="text-xs text-amber-600 font-medium">
                Revisión
              </span>
            </div>
            <div className="h-px w-8 bg-gray-200" />
            <div className="flex flex-col items-center gap-1">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-gray-300" />
              </div>
              <span className="text-xs text-gray-300">Aprobado</span>
            </div>
          </div>

          <p className="text-xs text-gray-400 mb-6">
            Esta página se actualiza automáticamente. Una vez aprobado,
            accederás al club de forma instantánea.
          </p>

          <div className="space-y-3">
            <Button
              onClick={handleCheckVerification}
              className="w-full"
              variant="outline"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Verificar estado
            </Button>

            {/* Retirar solicitud (doble confirmación) */}
            {!confirmingWithdraw ? (
              <button
                onClick={() => setConfirmingWithdraw(true)}
                className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Modificar mis datos
              </button>
            ) : (
              <div className="rounded-lg border border-red-100 bg-red-50 p-3 space-y-2">
                <p className="text-xs text-red-700 text-center font-medium">
                  Esto retirará tu solicitud. Tendrás que reenviarla con los
                  datos corregidos.
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleRetry}
                    disabled={retrying}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm py-1.5 h-auto"
                  >
                    {retrying ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Sí, retirar solicitud"
                    )}
                  </Button>
                  <button
                    onClick={() => setConfirmingWithdraw(false)}
                    className="flex-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
            {retryError && (
              <p className="text-sm text-red-600 text-center">{retryError}</p>
            )}

            <button
              onClick={handleSignOut}
              className="flex items-center justify-center gap-2 w-full text-sm text-gray-400 hover:text-gray-600 py-2"
            >
              <LogOut className="w-4 h-4" />
              Cerrar sesión
            </button>
          </div>
        </div>

        {/* Resumen de datos enviados */}
        {request && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
              Datos enviados
            </p>
            <div className="space-y-2 text-sm">
              {(request.firstName || request.lastName) && (
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                  <span>
                    {[request.firstName, request.lastName]
                      .filter(Boolean)
                      .join(" ")}
                  </span>
                </div>
              )}
              {request.phone && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                  <span>{request.phone}</span>
                </div>
              )}
              {request.birthDate && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                  <span>{formatDate(request.birthDate)}</span>
                </div>
              )}
              {request.address && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                  <span className="truncate">{request.address}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-400 pt-1 border-t border-gray-50">
                <span className="text-xs">
                  Enviado el{" "}
                  {request.createdAt.toLocaleDateString("es-CO", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
